import {
	useInfiniteQuery,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import client from "api/graphql/getClient";
import { streamIdPlaceholder } from "common/apiDefinitions";
import { graphql } from "gql/gql";
import {
	alertFieldsFragment,
	ListAlertsParams,
	SortDirection,
} from "gql/graphql";
import {
	getFlatAlerts,
	newestAlertCreated,
	oldestAlertCreated,
} from "utils/alertUtils";
import { useActiveAccount } from "utils/useActiveAccount";
import useSnackbar from "utils/useSnackbar";

const listAlertsDocument = graphql(/* GraphQL */ `
	query listAlerts($params: ListAlertsParams!) {
		listAlerts(params: $params) {
			...alertFields
			# TODO: https://samdesk.atlassian.net/browse/DEV-5029 see if we can just get a length value for cases like this (we just need an array to get the length)
		}
	}
`);

interface Args {
	before?: string;
	params: ListAlertsParams;
	userId?: string;
}

const listAlerts = async ({ before, userId, params }: Args) => {
	const updatedParams = { ...params };
	if (before) updatedParams.before = before;
	updatedParams.stream_ids = params.stream_ids.filter(
		(_id) => _id !== streamIdPlaceholder
	);

	updatedParams.sort_dir = params.sort_dir ?? SortDirection.DESC;

	const res = await client(userId).request(listAlertsDocument, {
		params: updatedParams,
	});
	return res.listAlerts;
};

const maxGapPagesPerFetch = 10;
const deltaOverlapMs = 30 * 1000;

/**
 * Ceiling on the alerts this query accumulates, so a long-lived pane on a busy
 * stream cannot grow the entry without bound. Trimming raises the entry's
 * oldest alert, which rolls the historical anchor forward through the same path
 * the gap-page bail uses, so the dropped alerts stay reachable by scrolling.
 */
export const maxAccumulatedAlerts = 10_000;

/**
 * Trim only once the entry overshoots the ceiling by this slack. Trimming on
 * every merge would move the entry's oldest alert on each tick at the cap,
 * re-keying the historical query every 30s: an extra page refetch per tick and
 * a reset for any reader scrolled into history. One latest page carries at
 * most 100 alerts, so 500 guarantees several quiet ticks between anchor rolls.
 */
export const trimSlackAlerts = 500;

export const fetchLatestAlerts = async ({
	getAccumulated,
	params,
	userId,
}: {
	getAccumulated: () => alertFieldsFragment[] | undefined;
	params: ListAlertsParams;
	userId?: string;
}): Promise<alertFieldsFragment[]> => {
	const accumulated = getAccumulated();
	const previousNewest = accumulated && newestAlertCreated(accumulated);

	if (!previousNewest) {
		return listAlerts({ before: new Date().toISOString(), params, userId });
	}

	// `backfill` is omitted rather than set to false: listAlerts requires
	// `before` alongside `backfill`, and the first delta page has no `before`.
	const overlapAfter = previousNewest.getTime() - deltaOverlapMs;
	const timeFilterAfter = params.after ? new Date(params.after).getTime() : 0;
	const deltaParams = {
		...params,
		after: new Date(Math.max(overlapAfter, timeFilterAfter)).toISOString(),
	};
	delete deltaParams.backfill;

	const pageSize = params.count ?? Infinity;
	const fetchedPages: alertFieldsFragment[][] = [];
	let before: string | undefined;

	while (fetchedPages.length < maxGapPagesPerFetch) {
		const page = await listAlerts({ before, params: deltaParams, userId });
		fetchedPages.push(page);

		const pageOldest = oldestAlertCreated(page);
		if (
			page.length < pageSize ||
			!pageOldest ||
			pageOldest.getTime() <= previousNewest.getTime()
		) {
			// Read the cache again instead of reusing the copy taken before the
			// fetch: a mark-read patch that landed while the request was in flight
			// would otherwise be overwritten by this merge, and the accumulated
			// alerts are never refetched to correct it.
			const merged = getFlatAlerts([...fetchedPages, getAccumulated() ?? []]);
			return merged.length > maxAccumulatedAlerts + trimSlackAlerts ?
					merged.slice(0, maxAccumulatedAlerts)
				:	merged;
		}

		before = pageOldest.toISOString();
	}

	return getFlatAlerts(fetchedPages);
};

export const useListAlertsLatest = (
	params: ListAlertsParams,
	criterionVersion: string | undefined
) => {
	const { _id, userId } = useActiveAccount();
	const { handleError } = useSnackbar();
	const queryClient = useQueryClient();

	const queryKey = listAlertsLatestQueryKey(
		userId,
		_id,
		params.stream_ids,
		params.after,
		criterionVersion
	);

	return useQuery(
		queryKey,
		() =>
			fetchLatestAlerts({
				getAccumulated: () =>
					queryClient.getQueryData<alertFieldsFragment[]>(queryKey),
				params,
				userId,
			}),
		{
			enabled: !!userId && params.stream_ids.length > 0 && !!criterionVersion,
			onError: (error) => handleError(error),
			refetchInterval: 30 * 1000,
			refetchIntervalInBackground: true,
			refetchOnWindowFocus: true,
		}
	);
};

export const useListAlertsHistorical = (
	params: ListAlertsParams,
	anchor: string | undefined,
	criterionVersion: string | undefined
) => {
	const { _id, userId } = useActiveAccount();
	const { handleError } = useSnackbar();

	return useInfiniteQuery(
		listAlertsHistoricalQueryKey(
			userId,
			_id,
			params.stream_ids,
			params.after,
			anchor,
			criterionVersion
		),
		({ pageParam }: { pageParam?: null | number }) =>
			listAlerts({
				before: pageParam ? new Date(pageParam).toISOString() : (anchor ?? ""),
				params,
				userId,
			}),
		{
			enabled:
				!!userId &&
				params.stream_ids.length > 0 &&
				!!anchor &&
				!!criterionVersion,
			getNextPageParam: (lastPage): number | undefined => {
				if (lastPage.length === 0) return;

				return oldestAlertCreated(lastPage)?.getTime() ?? 0;
			},
			onError: (error) => handleError(error),
			refetchOnWindowFocus: false,
			staleTime: Infinity,
		}
	);
};

export const listAlertsLatestQueryKey = (
	userId: string | undefined,
	_id: string | undefined,
	streamIds: string[],
	after?: string | null,
	criterionVersion?: string
) => {
	return [
		"user",
		userId,
		"account",
		_id,
		"alertsLatest",
		streamIds,
		"after",
		after,
		"criterion",
		criterionVersion,
	];
};

export const listAlertsHistoricalQueryKey = (
	userId: string | undefined,
	_id: string | undefined,
	streamIds: string[],
	after?: string | null,
	anchor?: string | null,
	criterionVersion?: string
) => {
	return [
		"user",
		userId,
		"account",
		_id,
		"alertsHistorical",
		streamIds,
		"after",
		after,
		"anchor",
		anchor,
		"criterion",
		criterionVersion,
	];
};
